/**
 * Everything that can be done to a binding map, as pure functions on one.
 *
 * Pure so conflict detection is unit-tested arithmetic rather than a property of a settings row:
 * the panel calls these and draws whatever came back, and a map these functions produced never
 * holds the same code twice. Refusing to produce a conflicting map is the point. A duplicate
 * binding is a bug the player cannot see afterwards, because the key still does something, just
 * not both of the things two rows claim.
 *
 * The serialised form is the map itself, actions to code lists, so the stored record reads as what
 * the player chose. `fromStored` is total: anything malformed falls back per action rather than
 * per map, and a code two actions somehow both stored goes to whichever the contract lists first.
 */

import { BINDABLE_ACTIONS } from '@/core/input/bindings/bindingContract';
import type { BindableActionId, BindingMap } from '@/core/input/bindings/bindingContract';
import { DEFAULT_BINDINGS } from '@/core/input/bindings/defaultBindings';

/**
 * The keys no binding may take.
 *
 * `Escape` stays the way out of everything, capture included, so a map where it fired a weapon
 * would be a game the player cannot leave. The function keys the browser owns are refused for the
 * honest reason that the game never sees them reliably: a binding that works until the browser
 * eats it is worse than a refusal with a reason.
 */
const RESERVED_CODES: readonly string[] = ['Escape', 'F5', 'F11', 'F12', 'MetaLeft', 'MetaRight'];

export function isReservedCode(code: string): boolean {
  return RESERVED_CODES.includes(code);
}

/** Which action currently holds a code, or null for a key that means nothing yet. */
export function holderOf(map: BindingMap, code: string): BindableActionId | null {
  return BINDABLE_ACTIONS.find((action) => map[action].includes(code)) ?? null;
}

export type RebindResult =
  /** The map with the action on its new key, the old synonyms replaced by the one chosen. */
  | { readonly kind: 'bound'; readonly map: BindingMap }
  /** The code is spoken for. Nothing changed; the caller offers the swap or the cancel. */
  | { readonly kind: 'conflict'; readonly holder: BindableActionId }
  /** The code may never be taken. Nothing changed. */
  | { readonly kind: 'reserved' };

export function rebind(map: BindingMap, action: BindableActionId, code: string): RebindResult {
  if (isReservedCode(code)) return { kind: 'reserved' };

  const holder = holderOf(map, code);
  if (holder !== null && holder !== action) return { kind: 'conflict', holder };

  return { kind: 'bound', map: { ...map, [action]: [code] } };
}

/**
 * The conflict resolved the loud way: the action takes the code, the holder loses it.
 *
 * The holder keeps any other synonyms it had, so stealing `ArrowUp` leaves `KeyW` walking. A
 * holder left with nothing is left with nothing, visibly: an empty row is what tells the player
 * which verb the swap disarmed, where a quiet default would rebind something they never chose.
 */
export function swapBind(map: BindingMap, action: BindableActionId, code: string): BindingMap {
  const holder = holderOf(map, code);
  const stripped =
    holder === null
      ? map
      : { ...map, [holder]: map[holder].filter((existing) => existing !== code) };
  return { ...stripped, [action]: [code] };
}

/** One row back to what it ships with. Codes the defaults reclaim are taken from their holders. */
export function resetBinding(map: BindingMap, action: BindableActionId): BindingMap {
  let next: BindingMap = { ...map, [action]: [] };
  for (const code of DEFAULT_BINDINGS[action]) {
    next = swapBind(next, action, code);
  }
  // `swapBind` binds one code at a time; the row wants the whole default list back.
  return { ...next, [action]: DEFAULT_BINDINGS[action] };
}

/** What the stored value is: the map itself, readable as what the player chose. */
export function toStored(map: BindingMap): string {
  return JSON.stringify(map);
}

/**
 * A stored map, made usable, or null when nothing usable is there.
 *
 * Null is the caller's cue to fall back to the defaults whole. A partial record is not null:
 * every action the record answers for legally keeps its answer, every one it does not falls back
 * alone, and a code claimed twice goes to the first claimant so the invariant holds on load too.
 */
export function fromStored(raw: unknown): BindingMap | null {
  if (typeof raw !== 'string') return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return null;

  const stored = new Map<string, unknown>(Object.entries(parsed));
  const record: Partial<Record<BindableActionId, readonly string[]>> = {};
  const taken = new Set<string>();
  // Walked in contract order rather than the file's, so a code claimed twice lands on the same
  // action whatever order a hand-edited save listed them in.
  for (const action of BINDABLE_ACTIONS) {
    const value = stored.get(action);
    if (!Array.isArray(value) || !value.every((code) => typeof code === 'string')) continue;
    const codes = value.filter((code) => !taken.has(code) && !isReservedCode(code));
    for (const code of codes) taken.add(code);
    record[action] = codes;
  }

  const map: Record<BindableActionId, readonly string[]> = { ...DEFAULT_BINDINGS };
  for (const action of BINDABLE_ACTIONS) {
    // A missing action falls back to its defaults, minus any default key the stored half already
    // spent elsewhere: the player's own choices outrank what a row they never touched ships with.
    map[action] = record[action] ?? DEFAULT_BINDINGS[action].filter((code) => !taken.has(code));
  }
  return map;
}
